import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { AuthBrandPanel } from "./LoginPage";

const inputClass =
  "h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,15}$/;

export default function RegisterPage() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", password: "", confirm: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{11}$/.test(form.phone)) {
      return setError("Phone number must be exactly 11 digits.");
    }
    if (!PASSWORD_RE.test(form.password)) {
      return setError("Password must be 8–15 characters with letters, numbers, and a special character.");
    }
    if (form.password !== form.confirm) {
      return setError("Passwords do not match.");
    }
    setLoading(true);
    try {
      await register({
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        password: form.password,
      });
      toast.success("Account created. Welcome to LearnYourWay.");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-bone pt-[72px] lg:grid-cols-2" data-testid="register-page">
      <AuthBrandPanel />
      <div className="flex items-center justify-center px-4 py-16 sm:px-8">
        <div className="w-full max-w-md">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Join us</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-forest-deep">
            Create your account
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            One account for consultations, resources, tutoring, and training.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="register-first-name-input" required className={inputClass} placeholder="First name"
                value={form.first_name} onChange={(e) => set("first_name", e.target.value)} autoComplete="given-name" />
              <input data-testid="register-last-name-input" required className={inputClass} placeholder="Last name"
                value={form.last_name} onChange={(e) => set("last_name", e.target.value)} autoComplete="family-name" />
            </div>
            <input data-testid="register-email-input" type="email" required autoComplete="email"
              className={inputClass} placeholder="Email address"
              value={form.email} onChange={(e) => set("email", e.target.value)} />
            <input data-testid="register-phone-input" type="tel" required autoComplete="tel"
              className={inputClass} placeholder="Phone number (11 digits)"
              value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            <input data-testid="register-password-input" type="password" required autoComplete="new-password"
              className={inputClass} placeholder="Password (8–15 chars, letters + numbers + symbol)"
              value={form.password} onChange={(e) => set("password", e.target.value)} />
            <input data-testid="register-confirm-input" type="password" required autoComplete="new-password"
              className={inputClass} placeholder="Re-enter password"
              value={form.confirm} onChange={(e) => set("confirm", e.target.value)} />
            {error && (
              <p data-testid="register-error" className="rounded-xl bg-destructive/10 px-4 py-2.5 text-sm font-medium text-destructive">
                {error}
              </p>
            )}
            <button type="submit" data-testid="register-submit-btn" disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-gold font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(212,175,55,0.4)] disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Create account
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-to-login-link" className="font-semibold text-forest transition-colors hover:text-gold-dark">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
